import * as React from "react";
import { useContext } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import CameraModal from './camera/CameraModal';
import StealthCamera from './camera/StealthCamera';
import StealthVideoRecorder from './camera/StealthVideoRecorder';
import HistoryManager from './dialogs/HistoryManager';
import UnifiedSettingsDialog from './dialogs/UnifiedSettingsDialog';
import AdminModal from './dialogs/AdminModal';
import { HistoryDrawerProvider, useHistoryDrawer } from '../contexts/historyDrawerContext';
import { useSettings } from '../contexts/settingsContext';
import {
  setLocalStorageEnumMap,
  clearLocalStorageEnumMap,
} from "../services/user/browserService.js";
import SlugSearchResults from "./people/SlugSearchResults";
import { useSearch } from "../contexts/searchContext";
import ConfirmationDialog from "./dialogs/ConfirmationDialogue.js";
import { CognitoUserContext } from "../contexts/cognitoUserContext";
import { detectFaceAttributes } from "../services/api/rekognitionService";
import { useSearchPeople } from "./search/useSearchPeople";
import SearchAppBar from "./search/SearchAppBar";
import FilterDrawer from "./search/FilterDrawer";

const drawerWidth = "auto";
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    ...(open && {
      transition: theme.transitions.create("margin", {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
    }),
  })
);

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

function SlugQueryManagerInner() {
  const { cognitoUser } = useContext(CognitoUserContext);
  const settings = useSettings();

  // Use unified search context
  const {
    datasetMap,
    setDatasetMap,
    selectedDataset,
    enumMap,
    setEnumMap,
    defaultMap,
    searchString,
    triggerSearch,
    setTriggerSearch,
    triggerAttributeSearch,
    setTriggerAttributeSearch,
    setFilteredPeople,
    setMatchSource
  } = useSearch();

  const { historyDrawerOpen, openHistoryDrawer, closeHistoryDrawer } = useHistoryDrawer();

  // Local state
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [cameraOpen, setCameraOpen] = React.useState(false);
  const [stealthCameraOpen, setStealthCameraOpen] = React.useState(false);
  const [stealthVideoOpen, setStealthVideoOpen] = React.useState(false);
  const [adminOpen, setAdminOpen] = React.useState(false);

  // Use search hook
  const {
    searchPeople,
    pullEntireSet,
    clearResults,
    peopleFromAPI,
    snackbarMessage,
    snackbarOpen,
    setSnackbarOpen,
    confirmOpen,
    setConfirmOpen
  } = useSearchPeople(setFilteredPeople, setMatchSource);


  const myarr = Array.from(enumMap);

  // Update Dataset in enumMap only when selectedDataset changes
  React.useEffect(() => {
    if (enumMap.get("Dataset") !== selectedDataset) {
      const updatedMap = new Map(enumMap);
      updatedMap.set("Dataset", selectedDataset);
      setEnumMap(updatedMap);
    }
  }, [selectedDataset, enumMap, setEnumMap]);

  // Listen for search triggers from other components (e.g., CameraModal)
  React.useEffect(() => {
    if (triggerSearch) {
      searchPeople(triggerSearch.enumMap, triggerSearch.searchString);
      setTriggerSearch(null);
    }
  }, [triggerSearch, searchPeople, setTriggerSearch]);

  // Listen for attribute search triggers
  React.useEffect(() => {
    if (triggerAttributeSearch) {
      handleAttributeSearch(triggerAttributeSearch.imageDataUrl);
      setTriggerAttributeSearch(null);
    }
  }, [triggerAttributeSearch, setTriggerAttributeSearch]);

  async function handleAttributeSearch(imageDataUrl) {
    try {
      const attributesResult = await detectFaceAttributes(imageDataUrl);

      if (attributesResult.faceCount > 0) {
        const detectedAttributes = attributesResult.faces[0].attributes;

        // Create new enumMap with detected attributes
        const newEnumMap = new Map();
        newEnumMap.set('Dataset', selectedDataset);

        if (detectedAttributes.gender) {
          newEnumMap.set('Gender', detectedAttributes.gender);
        }
        if (detectedAttributes.agegroup) {
          newEnumMap.set('AgeGroup', detectedAttributes.agegroup);
        }
        if (detectedAttributes.glasses) {
          newEnumMap.set('Glasses', detectedAttributes.glasses);
        }
        if (detectedAttributes.facialhair) {
          newEnumMap.set('FacialHair', detectedAttributes.facialhair);
        }

        setEnumMap(newEnumMap);

        const updatedDatasetMap = new Map(datasetMap);
        updatedDatasetMap.set(selectedDataset, newEnumMap);
        setDatasetMap(updatedDatasetMap);

        await searchPeople(newEnumMap, null);
      } else {
        setFilteredPeople(new Array(0));
        setMatchSource('manual_filter');
      }
    } catch (error) {
      console.error('Face attribute detection failed:', error);
      setFilteredPeople(new Array(0));
      setMatchSource('manual_filter');
    }
  }

  // Drawer handlers
  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    const tempMap = new Map(enumMap);
    tempMap.set("Dataset", selectedDataset);
    setLocalStorageEnumMap(tempMap);
    searchPeople(tempMap, searchString);
    setDrawerOpen(false);
  };

  const handleDrawerQuit = () => {
    setDrawerOpen(false);
  };

  const handleDrawerClear = () => {
    const clearMap = new Map();
    datasetMap.set(selectedDataset, clearMap);
    setEnumMap(clearMap);
    clearLocalStorageEnumMap();
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <CssBaseline />

      <SearchAppBar
        drawerOpen={drawerOpen}
        onHistoryOpen={openHistoryDrawer}
        onCameraOpen={() => setCameraOpen(true)}
        onVideoRecorderOpen={() => setStealthVideoOpen(true)}
        onStealthOpen={() => setStealthCameraOpen(true)}
        onDrawerOpen={handleDrawerOpen}
        onSettingsOpen={settings.openSettings}
        onAdminOpen={() => setAdminOpen(true)}
        cognitoUser={cognitoUser}
        stealthMode={settings.stealthMode}
        videoEnabled={settings.videoEnabled}
        myarr={myarr}
      />

      <CameraModal
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        username={cognitoUser && cognitoUser.username}
      />

      <StealthVideoRecorder
        open={stealthVideoOpen}
        onClose={() => setStealthVideoOpen(false)}
      />

      <StealthCamera
        open={stealthCameraOpen}
        onClose={() => setStealthCameraOpen(false)}
      />

      <FilterDrawer
        open={drawerOpen}
        onClose={handleDrawerQuit}
        onApply={handleDrawerClose}
        onClear={handleDrawerClear}
        enumMap={enumMap}
      />

      <HistoryManager
        open={historyDrawerOpen}
        onClose={closeHistoryDrawer}
      />

      <AdminModal
        open={adminOpen}
        onClose={() => setAdminOpen(false)}
      />

      <UnifiedSettingsDialog
        open={settings.settingsDialogOpen}
        onClose={settings.closeSettings}
        deleteConfirmation={settings.deleteConfirmation}
        onDeleteConfirmationChange={settings.updateDeleteConfirmation}
        autoEnableVoice={settings.autoEnableVoice}
        onAutoEnableVoiceChange={settings.updateAutoEnableVoice}
        stealthMode={settings.stealthMode}
        onStealthModeChange={settings.updateStealthMode}
        removeBg={settings.removeBg}
        setRemoveBg={settings.setRemoveBg}
        sliderValue={settings.sliderValue}
        setSliderValue={settings.setSliderValue}
        maxFacesValue={settings.maxFacesValue}
        setMaxFacesValue={settings.setMaxFacesValue}
        showBoundingBoxes={settings.showBoundingBoxes}
        setShowBoundingBoxes={settings.setShowBoundingBoxes}
        immediateFaceMatch={settings.immediateFaceMatch}
        setImmediateFaceMatch={settings.setImmediateFaceMatch}
        updateCharacteristicsOnMatch={settings.updateCharacteristicsOnMatch}
        setUpdateCharacteristicsOnMatch={settings.setUpdateCharacteristicsOnMatch}
        locationHighAccuracy={settings.locationHighAccuracy}
        setLocationHighAccuracy={settings.setLocationHighAccuracy}
        locationAcquisitionTimeout={settings.locationAcquisitionTimeout}
        setLocationAcquisitionTimeout={settings.setLocationAcquisitionTimeout}
        locationCacheMaxAge={settings.locationCacheMaxAge}
        setLocationCacheMaxAge={settings.setLocationCacheMaxAge}
        compressionQuality={settings.compressionQuality}
        setCompressionQuality={settings.setCompressionQuality}
        autoAdjustCompression={settings.autoAdjustCompression}
        setAutoAdjustCompression={settings.setAutoAdjustCompression}
        autoCreateEventOnAIMatch={settings.autoCreateEventOnAIMatch}
        setAutoCreateEventOnAIMatch={settings.setAutoCreateEventOnAIMatch}
        aggregatorEventLimit={settings.aggregatorEventLimit}
        setAggregatorEventLimit={settings.setAggregatorEventLimit}
        videoEnabled={settings.videoEnabled}
        setVideoEnabled={settings.setVideoEnabled}
        videoFrameInterval={settings.videoFrameInterval}
        setVideoFrameInterval={settings.setVideoFrameInterval}
      />

      <Main open={drawerOpen}>
        <DrawerHeader />
        <SlugSearchResults />
      </Main>

      <ConfirmationDialog
        title="Please Confirm"
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={pullEntireSet}
        onReject={() => clearResults(defaultMap, setEnumMap)}
      >
        <Typography>
          You will fetch {peopleFromAPI.length} results, is this OK?
        </Typography>
      </ConfirmationDialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="info"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default function SlugQueryManager() {
  return (
    <HistoryDrawerProvider>
      <SlugQueryManagerInner />
    </HistoryDrawerProvider>
  );
}
